import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { sessionRequestConfig } from "../../services/sessionLocal";

/**
 * Displays pending collaboration invitations for the current tenant.
 * Intended to be embedded in the dashboard or a settings page.
 */
export default function PendingInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [responding, setResponding] = useState(null); // invitation id being acted on

  const cfg = sessionRequestConfig();

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await axios.get("/api/collab/my-invitations", cfg);
      setInvitations(res?.data?.invitations || []);
    } catch (err) {
      console.error("Failed to fetch invitations:", err);
    }
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const respond = async (eventId, action) => {
    setResponding(eventId);
    try {
      await axios.post(`/api/collab/events/${eventId}/respond`, { action }, cfg);
      // Remove from list after successful response
      setInvitations((prev) => prev.filter((inv) => inv.eventid !== eventId));
    } catch (err) {
      console.error("Failed to respond to invitation:", err);
    } finally {
      setResponding(null);
    }
  };

  if (invitations.length === 0) return null;

  return (
    <div className="mb-4">
      <h3 className="text-base font-semibold mb-2">
        Pending Invitations ({invitations.length})
      </h3>
      <div className="space-y-2">
        {invitations.map((inv) => (
          <div
            key={inv.id}
            className="flex items-center justify-between p-3 rounded border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
          >
            <div>
              <div className="text-sm font-medium">
                {inv.from_tenant_name || "A tenant"} invited you to{" "}
                <strong>{inv.event_name || `Event #${inv.eventid}`}</strong>
              </div>
              <div className="text-xs text-neutral-500">
                Role: {inv.role} &middot;{" "}
                {new Date(inv.createdon).toLocaleDateString()}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1 text-sm rounded bg-green-600 text-white disabled:opacity-50"
                disabled={responding === inv.eventid}
                onClick={() => respond(inv.eventid, "accept")}
              >
                Accept
              </button>
              <button
                type="button"
                className="px-3 py-1 text-sm rounded border text-neutral-600 dark:text-neutral-300 disabled:opacity-50"
                disabled={responding === inv.eventid}
                onClick={() => respond(inv.eventid, "decline")}
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
